import React from 'react';
import { useSelector } from 'react-redux';
import { timeUntil } from 'helpers/PitchLimitHelper';
import styles from './ApplicationCreditsSection.module.scss';
import PropTypes from 'prop-types';

// Bar turns red at exactly 3 or fewer credits remaining
const LOW_CREDITS_THRESHOLD = 3;
// Expiry text/colour turns red at exactly 3 or fewer days remaining
const EXPIRING_SOON_DAYS = 3;

function daysUntil(dateStr) {
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const ApplicationCreditsSection = ({ mobile = false }) => {
  const { introsLeft, introsLeftMax, applicationCreditsRemaining } =
    useSelector((s) => s.messages);
  const user = useSelector((s) => s.auth.user);

  if (!user) return null;

  const showMonthlyBarCheck = user.pro && !user.premium && introsLeftMax > 1;
  const showCreditsBlock =
    user.has_purchased_credits && applicationCreditsRemaining > 0;

  if (!showCreditsBlock && !showMonthlyBarCheck) return null;

  const packs =
    (user.application_credits && user.application_credits.balances) || [];
  const totalCreditsPurchased = packs.reduce(
    (sum, p) => sum + p.credits_purchased,
    0
  );

  const isLowCount =
    applicationCreditsRemaining > 0 &&
    applicationCreditsRemaining <= LOW_CREDITS_THRESHOLD;
  const earliestPack = packs[0];
  const earliestExpiresDays = earliestPack
    ? daysUntil(earliestPack.expires_at)
    : null;

  // Pro monthly allowance bar — shown for Pro-but-not-Premium users
  const showMonthlyBar = showMonthlyBarCheck;
  const today = new Date();
  const joined = new Date(user.joined);
  const nextRenewal = new Date(
    today.getFullYear(),
    today.getMonth(),
    joined.getDate()
  );
  if (nextRenewal <= today) nextRenewal.setMonth(nextRenewal.getMonth() + 1);
  const renewsIn = timeUntil(today, nextRenewal);
  const proAllowanceExhausted = introsLeft === 0;
  const proBarWidth =
    introsLeftMax > 0 ? Math.round((introsLeft / introsLeftMax) * 100) : 0;

  const creditBarWidth =
    totalCreditsPurchased > 0
      ? Math.round((applicationCreditsRemaining / totalCreditsPurchased) * 100)
      : 0;

  return (
    <div className={mobile ? styles.sectionMobile : styles.section}>
      {showMonthlyBar && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.label}>Monthly applications</span>
            <span className={styles.count}>
              {introsLeft}/{introsLeftMax}
            </span>
          </div>
          <div className={styles.barTrack}>
            <div
              className={`${styles.bar} ${styles.barOrange}`}
              style={{ width: `${proBarWidth}%` }}
            />
          </div>
          <p className={styles.subtext}>
            {proAllowanceExhausted
              ? `Allowance used · resets in ${renewsIn}`
              : `${introsLeft} application${
                  introsLeft === 1 ? '' : 's'
                } remaining · resets in ${renewsIn}`}
          </p>
        </div>
      )}

      {showCreditsBlock && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.label}>
              {showMonthlyBar ? 'Pay As You Go credits' : 'Application credits'}
            </span>
            <span
              className={`${styles.count} ${
                isLowCount ? styles.textDanger : ''
              }`}
            >
              {applicationCreditsRemaining}/{totalCreditsPurchased}
            </span>
          </div>
          <div className={styles.barTrack}>
            <div
              className={`${styles.bar} ${
                isLowCount ? styles.barRed : styles.barBlue
              }`}
              style={{ width: `${creditBarWidth}%` }}
            />
          </div>
          {packs.length > 1 ? (
            <>
              <p className={styles.subtext}>
                Across {packs.length} credit packs
              </p>
              <table className={styles.packTable}>
                <tbody>
                  {packs.map((pack, i) => {
                    const days = daysUntil(pack.expires_at);
                    const packExpiring = days <= EXPIRING_SOON_DAYS;
                    return (
                      <tr key={i}>
                        <td className={styles.packLabel}>
                          Pack {i + 1}:{' '}
                          <strong>{pack.credits_remaining} left</strong>
                        </td>
                        <td
                          className={`${styles.packExpiry} ${
                            packExpiring ? styles.textDanger : styles.textMuted
                          }`}
                        >
                          Expires in {days} day{days === 1 ? '' : 's'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : earliestPack ? (
            <p className={styles.subtext}>
              {isLowCount
                ? `Only ${applicationCreditsRemaining} left · expires in ${earliestExpiresDays} day${
                    earliestExpiresDays === 1 ? '' : 's'
                  } (${formatDate(earliestPack.expires_at)})`
                : `Expires in ${earliestExpiresDays} day${
                    earliestExpiresDays === 1 ? '' : 's'
                  } (${formatDate(earliestPack.expires_at)})`}
            </p>
          ) : null}
          <a href="/pricing/creative" className={styles.cta}>
            Buy more credits →
          </a>
        </div>
      )}
    </div>
  );
};

ApplicationCreditsSection.propTypes = {
  mobile: PropTypes.bool,
};

export default ApplicationCreditsSection;
